import { ReactNode, useState } from "react";
import {
  CreativeAspect,
  CreativeFinish,
  CreativeJob,
  CreativeOutputRequest,
  CreativeOutputType,
} from "api/creativeStudio";
import useCreateCreativeJobMutation from "api/hooks/useCreateCreativeJobMutation";
import { extractApiErrorMessage } from "utils/util";
import KlydosPickDialog from "views/Gifts/GiftCategories/components/KlydosPickDialog";
import useGetKlydosByIds from "views/Device/hooks/useGetKlydosByIds";
import DSAlert from "views/system-design/DSAlert";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSDialog from "views/system-design/DSDialog";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import DSSlider from "views/system-design/DSSlider";
import DSStack from "views/system-design/DSStack";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import sectionCardSx from "views/system-design/sectionCardSx";
import {
  ASPECT_OPTIONS,
  COST_ESTIMATES,
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  DESCRIPTION_PLACEHOLDERS,
  FINISH_OPTIONS,
  IMAGE_COUNT,
  KLYDO_DURATION,
  MAX_KLYDOS,
  OUTPUT_TYPE_NOUNS,
  OUTPUT_TYPE_OPTIONS,
  VIDEO_DURATION,
  averageCostUsd,
  formatUsd,
} from "../utils/creativeOptions";
import CreativeCollectionPicker, {
  PickedCollection,
} from "./CreativeCollectionPicker";
import CreativeReferenceImages, {
  ReferenceImage,
} from "./CreativeReferenceImages";

const Field = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <DSBox>
    <DSTypography sx={{ fontWeight: 600, mb: 0.5 }}>{title}</DSTypography>
    {hint && (
      <DSTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hint}
      </DSTypography>
    )}
    {children}
  </DSBox>
);

type Props = {
  /** Recent jobs, for the average cost. */
  jobs: CreativeJob[];
};

const CreativeJobForm = ({ jobs }: Props) => {
  const [description, setDescription] = useState("");
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [outputType, setOutputType] = useState<CreativeOutputType>("images");
  const [imageCount, setImageCount] = useState(IMAGE_COUNT.default);
  const [videoDuration, setVideoDuration] = useState(VIDEO_DURATION.default);
  const [klydoDuration, setKlydoDuration] = useState(KLYDO_DURATION.default);
  const [aspect, setAspect] = useState<CreativeAspect>("1:1");
  const [finish, setFinish] = useState<CreativeFinish>("any");
  const [collection, setCollection] = useState<PickedCollection | null>(null);
  const [klydoIds, setKlydoIds] = useState<string[]>([]);
  const [klydoPickerOpen, setKlydoPickerOpen] = useState(false);
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [started, setStarted] = useState(false);

  const createJob = useCreateCreativeJobMutation();
  const { klydos: pickedKlydos } = useGetKlydosByIds(klydoIds);

  const isKlydo = outputType === "klydo";
  const trimmedLength = description.trim().length;
  const descriptionError =
    trimmedLength < DESCRIPTION_MIN
      ? `Please write at least ${DESCRIPTION_MIN} characters`
      : trimmedLength > DESCRIPTION_MAX
        ? `Please keep it under ${DESCRIPTION_MAX} characters`
        : null;
  const uploading = referenceImages.some((i) => i.status === "uploading");
  const uploadFailed = referenceImages.some((i) => i.status === "error");
  const canSubmit = !uploading && !uploadFailed && !createJob.isPending;

  const averageAll = averageCostUsd(jobs);
  const averageForType = averageCostUsd(jobs, outputType);

  const buildOutput = (): CreativeOutputRequest => {
    switch (outputType) {
      case "images":
        return { type: "images", count: imageCount, aspect };
      case "video":
        return { type: "video", durationSec: videoDuration, aspect };
      default:
        return { type: "klydo", durationSec: klydoDuration };
    }
  };

  const submit = () => {
    const inputImages = referenceImages
      .filter((image) => image.status === "done" && image.url)
      .map((image) => image.url as string);

    createJob.mutate(
      {
        description: description.trim(),
        output: buildOutput(),
        finish,
        // A klydo animation is new art, so it does not show existing klydos.
        ...(!isKlydo && collection && { collectionId: collection.id }),
        ...(!isKlydo && klydoIds.length > 0 && { klydoIds }),
        ...(inputImages.length > 0 && { inputImages }),
      },
      {
        onSuccess: () => {
          // Clear what was written for this creative; keep the output settings for the next one.
          setDescription("");
          setDescriptionTouched(false);
          setReferenceImages([]);
          setKlydoIds([]);
          setCollection(null);
          setStarted(true);
          setConfirmOpen(false);
        },
        onError: () => setConfirmOpen(false),
      },
    );
  };

  return (
    <DSCard sx={sectionCardSx}>
      <DSSectionHeading>New creative</DSSectionHeading>
      <DSCardContent>
        <DSStack spacing={3}>
          <Field title="What should it be?">
            <DSSegmentedTabs<CreativeOutputType>
              options={OUTPUT_TYPE_OPTIONS}
              value={outputType}
              onChange={(value) => value && setOutputType(value)}
            />
            {isKlydo && (
              <DSTypography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Made as a perfect 1080×1080 loop with matching theme colors.
                Review the preview, then upload the loop in Studio.
              </DSTypography>
            )}
          </Field>

          <Field
            title="Describe what you want"
            hint="Write it like you would brief a designer: the scene, the mood, any text to show."
          >
            <DSTextField
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              onBlur={() => setDescriptionTouched(true)}
              placeholder={DESCRIPTION_PLACEHOLDERS[outputType]}
              multiline
              minRows={4}
              fullWidth
              error={descriptionTouched && !!descriptionError}
              helperText={
                descriptionTouched && descriptionError
                  ? descriptionError
                  : `${trimmedLength} / ${DESCRIPTION_MAX}`
              }
            />
          </Field>

          {outputType === "images" && (
            <Field title="How many images?">
              <DSSegmentedTabs<string>
                size="small"
                options={Array.from(
                  { length: IMAGE_COUNT.max - IMAGE_COUNT.min + 1 },
                  (_, i) => {
                    const count = IMAGE_COUNT.min + i;
                    return {
                      value: String(count),
                      label: `${count} image${count === 1 ? "" : "s"}`,
                    };
                  },
                )}
                value={String(imageCount)}
                onChange={(value) => value && setImageCount(Number(value))}
              />
            </Field>
          )}

          {outputType === "video" && (
            <Field title={`How long? ${videoDuration} seconds`}>
              <DSSlider
                value={videoDuration}
                min={VIDEO_DURATION.min}
                max={VIDEO_DURATION.max}
                step={1}
                marks={[
                  {
                    value: VIDEO_DURATION.min,
                    label: `${VIDEO_DURATION.min}s`,
                  },
                  {
                    value: VIDEO_DURATION.max,
                    label: `${VIDEO_DURATION.max}s`,
                  },
                ]}
                valueLabelDisplay="auto"
                onChange={(_event, value) => setVideoDuration(value as number)}
                sx={{ maxWidth: 480 }}
              />
            </Field>
          )}

          {isKlydo && (
            <Field title={`How long is one loop? ${klydoDuration} seconds`}>
              <DSSlider
                value={klydoDuration}
                min={KLYDO_DURATION.min}
                max={KLYDO_DURATION.max}
                step={1}
                marks={[
                  {
                    value: KLYDO_DURATION.min,
                    label: `${KLYDO_DURATION.min}s`,
                  },
                  {
                    value: KLYDO_DURATION.max,
                    label: `${KLYDO_DURATION.max}s`,
                  },
                ]}
                valueLabelDisplay="auto"
                onChange={(_event, value) => setKlydoDuration(value as number)}
                sx={{ maxWidth: 480 }}
              />
            </Field>
          )}

          {!isKlydo && (
            <Field title="Shape" hint="Pick where you will post it.">
              <DSSegmentedTabs<CreativeAspect>
                size="small"
                options={ASPECT_OPTIONS}
                value={aspect}
                onChange={(value) => value && setAspect(value)}
              />
            </Field>
          )}

          <Field
            title="Wood finish"
            hint={
              isKlydo
                ? "Only used for the preview on the clock."
                : "Which Klydoclock to show."
            }
          >
            <DSSegmentedTabs<CreativeFinish>
              size="small"
              options={FINISH_OPTIONS}
              value={finish}
              onChange={(value) => value && setFinish(value)}
            />
          </Field>

          {!isKlydo && (
            <Field
              title="What plays on the clock screen (optional)"
              hint="Pick a collection to feature its name and logo, and its klydos play on the screen. Pick klydos to choose exactly what plays. Leave both empty to use our newest klydos."
            >
              <DSStack spacing={1.5}>
                <DSBox sx={{ maxWidth: 420 }}>
                  <CreativeCollectionPicker
                    value={collection}
                    onChange={setCollection}
                  />
                </DSBox>
                <DSBox
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <DSSecondaryButton
                    size="small"
                    onClick={() => setKlydoPickerOpen(true)}
                  >
                    {klydoIds.length
                      ? `Change klydos (${klydoIds.length})`
                      : "Pick klydos"}
                  </DSSecondaryButton>
                  {klydoIds.map((id) => (
                    <DSChip
                      key={id}
                      size="small"
                      label={
                        pickedKlydos.find((klydo) => klydo.id === id)?.name ||
                        id
                      }
                      onDelete={() =>
                        setKlydoIds((ids) => ids.filter((item) => item !== id))
                      }
                    />
                  ))}
                </DSBox>
              </DSStack>
            </Field>
          )}

          <Field
            title="Reference images (optional)"
            hint="A photo, a mood board or a style you like. Up to 6."
          >
            <CreativeReferenceImages
              images={referenceImages}
              onChange={setReferenceImages}
              disabled={createJob.isPending}
            />
          </Field>

          <DSAlert severity="warning">
            Each creative is made by Claude AI and costs real money: about $1–3
            per image set and $2–6 per video. Please only run what you need.
            Klydo animations cost about {COST_ESTIMATES.klydo}.
            {averageAll !== null && (
              <>
                {" "}
                <strong>Average so far: {formatUsd(averageAll)}</strong>
              </>
            )}
          </DSAlert>

          {createJob.isError && (
            <DSAlert severity="error">
              {extractApiErrorMessage(
                createJob.error,
                "Could not start the creative",
              )}
            </DSAlert>
          )}
          {uploadFailed && (
            <DSAlert severity="error">
              Some reference images did not upload. Remove them to continue.
            </DSAlert>
          )}

          <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DSPrimaryButton
              disabled={!canSubmit}
              onClick={() => {
                setDescriptionTouched(true);
                if (descriptionError) return;
                createJob.reset();
                setStarted(false);
                setConfirmOpen(true);
              }}
            >
              Make it
            </DSPrimaryButton>
            {uploading && (
              <DSTypography variant="body2" color="text.secondary">
                Waiting for the images to upload…
              </DSTypography>
            )}
          </DSBox>

          {started && (
            <DSAlert severity="success" onClose={() => setStarted(false)}>
              Started. It is ready in about 5–20 minutes, and the results show
              up in Recent creatives below. You can leave this page; nothing
              needs to be copied.
            </DSAlert>
          )}
        </DSStack>
      </DSCardContent>

      <DSDialog
        open={confirmOpen}
        onClose={() => !createJob.isPending && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>
          Start this {OUTPUT_TYPE_NOUNS[outputType]}?
        </DSDialogTitle>
        <DSDialogContent>
          <DSTypography sx={{ mb: 1 }}>
            Claude AI makes it, and it costs real money: about{" "}
            <strong>{COST_ESTIMATES[outputType]}</strong> for this{" "}
            {OUTPUT_TYPE_NOUNS[outputType]}.
          </DSTypography>
          {averageForType !== null && (
            <DSTypography variant="body2" color="text.secondary">
              Average so far for a {OUTPUT_TYPE_NOUNS[outputType]}:{" "}
              {formatUsd(averageForType)}
            </DSTypography>
          )}
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton
            onClick={() => setConfirmOpen(false)}
            disabled={createJob.isPending}
          >
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={submit}
            disabled={createJob.isPending}
            startIcon={
              createJob.isPending ? <DSCircularProgress size={16} /> : null
            }
          >
            Yes, start it
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>

      {klydoPickerOpen && (
        <KlydosPickDialog
          title="What plays on the clock screen"
          saveLabel="Use these klydos"
          selectedKlydos={klydoIds}
          limit={MAX_KLYDOS}
          onClose={() => setKlydoPickerOpen(false)}
          onPickKlydos={(ids) => {
            setKlydoIds(ids.slice(0, MAX_KLYDOS));
            setKlydoPickerOpen(false);
          }}
        />
      )}
    </DSCard>
  );
};

export default CreativeJobForm;
